import { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Star, StarOff, Eye, EyeOff, Pencil, Trash2, RotateCcw, ArrowUp, ArrowDown, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Pagination from "@/components/admin/Pagination";
import ConfirmActionDialog from "@/components/admin/ConfirmActionDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// M6 curated categories (admin picker). Legacy values still render in list but aren't offered in the picker.
const M6_CATEGORIES = [
  ["residential_exterior", "Residential Exterior"],
  ["residential_interior", "Residential Interior"],
  ["kitchens", "Kitchens"],
  ["bathrooms", "Bathrooms"],
  ["bedrooms", "Bedrooms"],
  ["commercial", "Commercial"],
  ["new_builds", "New Builds"],
  ["renovations", "Renovations"],
  ["feature_colours", "Feature Colours"],
  ["featured_projects", "Featured Projects"],
];

const ALL_CATEGORY_FILTERS = [
  ["all", "All categories"],
  ...M6_CATEGORIES,
  // Legacy values, kept for filtering existing rows
  ["interior", "Interior (legacy)"],
  ["exterior", "Exterior (legacy)"],
  ["kitchen", "Kitchen (legacy)"],
  ["bathroom", "Bathroom (legacy)"],
  ["bedroom", "Bedroom (legacy)"],
];

const PROJECT_TYPES = [
  ["", "—"],
  ["home", "Home"],
  ["commercial", "Commercial"],
  ["school", "School"],
  ["church", "Church"],
  ["office", "Office"],
  ["warehouse", "Warehouse"],
  ["retail", "Retail"],
  ["apartment", "Apartment"],
  ["industrial", "Industrial"],
];

const STATUS_FILTERS = [
  ["published", "Published"],
  ["unpublished", "Unpublished"],
  ["featured", "Featured"],
  ["deleted", "Deleted"],
  ["all", "All"],
];

const labelForCategory = (slug) => {
  const match = [...M6_CATEGORIES, ["interior", "Interior"], ["exterior", "Exterior"],
    ["kitchen", "Kitchen"], ["bathroom", "Bathroom"], ["bedroom", "Bedroom"]].find((c) => c[0] === slug);
  return match ? match[1] : slug;
};

async function fileToDataURL(file, maxDim = 1600, quality = 0.82) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = dataUrl;
  });
  let { width, height } = img;
  if (Math.max(width, height) > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

const relTime = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function StatusPill({ item }) {
  if (item.deleted_at) {
    return <span className="inline-block px-2 py-0.5 text-xs bg-rose-50 text-rose-700 border border-rose-100" data-testid="pill-deleted">deleted</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {item.is_featured && (
        <span className="inline-block px-2 py-0.5 text-xs bg-amber-50 text-amber-700 border border-amber-100" data-testid="pill-featured">featured</span>
      )}
      {item.is_published ? (
        <span className="inline-block px-2 py-0.5 text-xs bg-brand-success/10 text-brand-success border border-brand-success/25" data-testid="pill-published">published</span>
      ) : (
        <span className="inline-block px-2 py-0.5 text-xs bg-stone-100 text-stone-700 border border-stone-200" data-testid="pill-unpublished">unpublished</span>
      )}
    </div>
  );
}

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("published");
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null => create
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        status,
      });
      if (q.trim()) params.set("q", q.trim());
      if (category !== "all") params.set("category", category);
      const r = await api.get(`/admin/gallery?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, category, status]);

  useEffect(() => { load(); }, [load]);

  const toggleFeature = async (item) => {
    try {
      const path = item.is_featured ? "unfeature" : "feature";
      await api.post(`/admin/gallery/${item.id}/${path}`);
      toast.success(item.is_featured ? "Unfeatured" : "Featured");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  const togglePublish = async (item) => {
    try {
      const path = item.is_published ? "unpublish" : "publish";
      await api.post(`/admin/gallery/${item.id}/${path}`);
      toast.success(item.is_published ? "Unpublished" : "Published");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  const restore = async (item) => {
    try {
      await api.post(`/admin/gallery/${item.id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  const move = async (item, direction) => {
    const idx = items.findIndex((x) => x.id === item.id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const a = items[idx];
    const b = items[swapIdx];
    // Assign explicit sort_orders if missing; use existing if present.
    const aOrder = (a.sort_order ?? idx) + 0;
    const bOrder = (b.sort_order ?? swapIdx) + 0;
    try {
      await api.post(`/admin/gallery/reorder`, {
        items: [
          { id: a.id, sort_order: bOrder },
          { id: b.id, sort_order: aOrder },
        ],
      });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  const confirmSoftDelete = async (reason) => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.post(`/admin/gallery/${deleteTarget.id}/soft-delete`, { reason });
      toast.success("Gallery item moved to deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-brand-linen p-4">
        <Input
          data-testid="gallery-search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Search title or description…"
          className="max-w-xs"
        />
        <select
          data-testid="gallery-cat-filter"
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="border border-brand-linen bg-white px-3 py-2 text-sm"
        >
          {ALL_CATEGORY_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="flex flex-wrap gap-1 ml-auto">
          {STATUS_FILTERS.map(([v, l]) => (
            <button
              key={v}
              data-testid={`gallery-status-${v}`}
              onClick={() => { setStatus(v); setPage(1); }}
              className={`px-3 py-1.5 text-xs border tracking-wider ${status === v
                ? "border-brand-ink bg-brand-ink text-white"
                : "border-brand-linen bg-white text-brand-ink hover:bg-brand-linen/40"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <Button
          data-testid="gallery-add-btn"
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="ml-2"
        >
          <Plus className="w-4 h-4 mr-1" /> Add gallery item
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-brand-linen" data-testid="gallery-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-brand-linen text-xs uppercase tracking-widest text-brand-muted">
          <div className="col-span-1">Order</div>
          <div className="col-span-1">Image</div>
          <div className="col-span-3">Title</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Updated</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {loading && (
          <div className="px-4 py-6 text-sm text-brand-muted" data-testid="gallery-loading">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-brand-muted" data-testid="gallery-empty">
            No gallery items match the current filters.
          </div>
        )}

        {items.map((it, idx) => (
          <div
            key={it.id}
            data-testid={`gallery-row-${it.id}`}
            className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-brand-linen/60 items-center"
          >
            <div className="col-span-1 text-xs text-brand-muted flex flex-col gap-1">
              <span data-testid={`gallery-order-${it.id}`}>#{idx + 1 + (page - 1) * pageSize}</span>
              <div className="flex gap-1">
                <button
                  data-testid={`gallery-reorder-up-${it.id}`}
                  onClick={() => move(it, "up")}
                  disabled={idx === 0 || status === "deleted"}
                  className="p-1 border border-brand-linen disabled:opacity-30 hover:bg-brand-linen/40"
                  aria-label="Move up"
                >
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button
                  data-testid={`gallery-reorder-down-${it.id}`}
                  onClick={() => move(it, "down")}
                  disabled={idx === items.length - 1 || status === "deleted"}
                  className="p-1 border border-brand-linen disabled:opacity-30 hover:bg-brand-linen/40"
                  aria-label="Move down"
                >
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>
            </div>
            <div className="col-span-1">
              <GalleryThumb id={it.id} title={it.title} />
            </div>
            <div className="col-span-3">
              <div className="text-sm text-brand-ink">{it.title}</div>
              {it.description && <div className="text-xs text-brand-muted line-clamp-2">{it.description}</div>}
              {it.project_type && (
                <div className="text-[10px] uppercase tracking-widest text-brand-muted mt-1" data-testid={`gallery-project-type-${it.id}`}>
                  {it.project_type}
                </div>
              )}
            </div>
            <div className="col-span-2 text-xs">{labelForCategory(it.category)}</div>
            <div className="col-span-2"><StatusPill item={it} /></div>
            <div className="col-span-1 text-xs text-brand-muted" title={it.updated_at || it.created_at}>
              {relTime(it.updated_at || it.created_at)}
            </div>
            <div className="col-span-2 flex flex-wrap gap-1 justify-end">
              {!it.deleted_at && (
                <>
                  <IconBtn
                    testId={`gallery-action-${it.is_featured ? "unfeature" : "feature"}-${it.id}`}
                    title={it.is_featured ? "Unfeature" : "Feature"}
                    onClick={() => toggleFeature(it)}
                  >
                    {it.is_featured ? <StarOff className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
                  </IconBtn>
                  <IconBtn
                    testId={`gallery-action-${it.is_published ? "unpublish" : "publish"}-${it.id}`}
                    title={it.is_published ? "Unpublish" : "Publish"}
                    onClick={() => togglePublish(it)}
                  >
                    {it.is_published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </IconBtn>
                  <IconBtn
                    testId={`gallery-action-edit-${it.id}`}
                    title="Edit"
                    onClick={() => { setEditing(it); setFormOpen(true); }}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </IconBtn>
                  <IconBtn
                    testId={`gallery-action-soft-delete-${it.id}`}
                    title="Soft delete"
                    onClick={() => setDeleteTarget(it)}
                    destructive
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </IconBtn>
                </>
              )}
              {it.deleted_at && (
                <IconBtn
                  testId={`gallery-action-restore-${it.id}`}
                  title="Restore"
                  onClick={() => restore(it)}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </IconBtn>
              )}
            </div>
          </div>
        ))}
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />

      <GalleryFormDialog
        open={formOpen}
        editing={editing}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={() => { setFormOpen(false); setEditing(null); load(); }}
      />

      <ConfirmActionDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmSoftDelete}
        title="Soft delete gallery item"
        description={deleteTarget ? `"${deleteTarget.title}" will be hidden from the public gallery. You can restore it later.` : ""}
        confirmLabel="Soft delete"
        destructive
        reasonRequired
        reasonMinLength={10}
        reasonLabel="Reason (required, ≥10 characters)"
        reasonPlaceholder="Why is this being removed?"
        testId="dialog-gallery-delete"
        busy={busy}
      />
    </div>
  );
}

function IconBtn({ children, testId, title, onClick, destructive }) {
  return (
    <button
      data-testid={testId}
      title={title}
      onClick={onClick}
      className={`p-1.5 border ${destructive ? "border-rose-200 text-rose-600 hover:bg-rose-50" : "border-brand-linen text-brand-ink hover:bg-brand-linen/50"}`}
    >
      {children}
    </button>
  );
}

function GalleryThumb({ id, title }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get(`/admin/gallery/${id}`).then((r) => { if (mounted) setSrc(r.data?.image || null); }).catch(() => {});
    return () => { mounted = false; };
  }, [id]);
  if (!src) return <div className="w-14 h-14 bg-brand-linen/50 border border-brand-linen" />;
  return <img src={src} alt={title} className="w-14 h-14 object-cover border border-brand-linen" data-testid={`gallery-thumb-${id}`} />;
}

function GalleryFormDialog({ open, editing, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(M6_CATEGORIES[0][0]);
  const [projectType, setProjectType] = useState("");
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setTitle(editing.title || "");
        setDescription(editing.description || "");
        setCategory(editing.category || M6_CATEGORIES[0][0]);
        setProjectType(editing.project_type || "");
        setImage(null);
        // Fetch full image for preview in edit mode
        api.get(`/admin/gallery/${editing.id}`).then((r) => setImage(r.data?.image || null)).catch(() => {});
      } else {
        setTitle(""); setDescription(""); setCategory(M6_CATEGORIES[0][0]);
        setProjectType(""); setImage(null);
      }
    }
  }, [open, editing]);

  const onDrop = useCallback(async (files) => {
    if (!files[0]) return;
    const d = await fileToDataURL(files[0]);
    setImage(d);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { "image/*": [] }, maxFiles: 1 });

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!editing && !image) { toast.error("Image is required"); return; }
    setBusy(true);
    try {
      if (editing) {
        const body = { title: title.trim(), description, category };
        if (projectType) body.project_type = projectType;
        if (image && image.startsWith("data:")) body.image = image;
        await api.patch(`/admin/gallery/${editing.id}`, body);
        toast.success("Gallery item updated");
      } else {
        const body = { title: title.trim(), description, category, image };
        if (projectType) body.project_type = projectType;
        await api.post(`/admin/gallery`, body);
        toast.success("Gallery item added");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="dialog-gallery-form" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle data-testid="dialog-gallery-form-title">
            {editing ? "Edit gallery item" : "Add gallery item"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="space-y-3">
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Title *</div>
              <Input data-testid="gallery-form-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Description</div>
              <Textarea data-testid="gallery-form-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Category *</div>
              <select
                data-testid="gallery-form-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border border-brand-linen bg-white px-3 py-2 text-sm"
              >
                {M6_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Project type (optional)</div>
              <select
                data-testid="gallery-form-project-type"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
                className="w-full border border-brand-linen bg-white px-3 py-2 text-sm"
              >
                {PROJECT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest text-brand-muted">Photo</div>
            <div
              {...getRootProps()}
              data-testid="gallery-form-dropzone"
              className={`border-2 border-dashed p-6 text-center cursor-pointer ${isDragActive ? "border-brand-sage bg-brand-linen/40" : "border-brand-linen"}`}
            >
              <input {...getInputProps()} />
              {image ? (
                <div className="relative">
                  <img src={image} alt="" className="w-full h-48 object-cover" data-testid="gallery-form-preview" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setImage(null); }}
                    className="absolute top-1 right-1 bg-white/90 p-1 border border-brand-linen"
                    aria-label="Remove image"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto mb-2 text-brand-sage" size={22} />
                  <p className="text-xs">Drop or click to add photo</p>
                  {editing && <p className="text-[10px] mt-1 text-brand-muted">Leave empty to keep the existing image.</p>}
                </>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-4">
          <Button data-testid="dialog-gallery-form-cancel" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="dialog-gallery-form-submit" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : (editing ? "Save changes" : "Add item")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
